import {
  ATR,
  BollingerBands,
  EMA,
  MACD,
  RSI,
  SMA,
  VWAP,
} from "technicalindicators";

/**
 * Server-side technical-indicator math for the Hearth chart UI and
 * future signal-alert scanner. Wraps the `technicalindicators` npm
 * package with two contract shifts that the rest of the system relies
 * on:
 *
 * 1. **Length parity.** `technicalindicators` returns shorter arrays
 *    than its inputs — the warm-up bars (where the moving window
 *    hasn't filled yet) are dropped entirely. We left-pad with `null`
 *    so every output series has the same length as the input bars.
 *    Result: `bars[i]` and `indicators.sma_20[i]` always describe the
 *    same date; the SPA never has to compute offsets.
 *
 * 2. **Null-not-NaN.** VWAP can divide by zero on a bar with no
 *    volume; the lib will return NaN. We map any non-finite output to
 *    `null` so downstream JSON serialization is well-defined (JSON has
 *    no NaN).
 *
 * Indicator computation is pure — no I/O — and runs in the same
 * Lambda invocation as the bars query. Costs single-digit ms even
 * across 5y of daily data on Lambda's Node runtime.
 */

export interface OhlcvBar {
  date: string;
  open?: number;
  high?: number;
  low?: number;
  close: number;
  volume?: number;
}

export type ScalarSeries = (number | null)[];

export interface MacdPoint {
  macd: number | null;
  signal: number | null;
  histogram: number | null;
}

export interface BollingerPoint {
  upper: number | null;
  middle: number | null;
  lower: number | null;
}

/**
 * The exact set of indicator keys the API accepts. Periods are baked
 * into the key (e.g., `sma_20` not `sma`+`?period=20`) so the URL
 * shape is opaque-to-the-server-but-opinionated and the cache-key on
 * the SPA side stays stable for a given selection.
 */
export const INDICATOR_KEYS = [
  "sma_20",
  "sma_50",
  "sma_200",
  "ema_20",
  "ema_50",
  "ema_200",
  "rsi_14",
  "macd_12_26_9",
  "bb_20_2",
  "atr_14",
  "vwap",
] as const;

export type IndicatorKey = (typeof INDICATOR_KEYS)[number];

const INDICATOR_KEY_SET: ReadonlySet<string> = new Set(INDICATOR_KEYS);

export class UnknownIndicatorError extends Error {
  readonly key: string;
  constructor(key: string) {
    super(
      `Unknown indicator key '${key}'. Allowed: ${INDICATOR_KEYS.join(", ")}`,
    );
    this.name = "UnknownIndicatorError";
    this.key = key;
  }
}

/**
 * Parse a comma-separated `?indicators=` query param into a
 * deduped, validated array of indicator keys.
 *
 * - undefined / null / empty / all-whitespace → []
 * - whitespace around tokens is trimmed
 * - duplicates are folded (preserves first-seen order)
 * - unknown keys throw `UnknownIndicatorError` (the prices handler
 *   converts this to a 400 BadRequest)
 */
export function parseIndicatorParam(
  raw: string | undefined | null,
): IndicatorKey[] {
  if (!raw) return [];
  const seen = new Set<IndicatorKey>();
  const ordered: IndicatorKey[] = [];
  for (const part of raw.split(",")) {
    const k = part.trim();
    if (!k) continue;
    if (!INDICATOR_KEY_SET.has(k)) {
      throw new UnknownIndicatorError(k);
    }
    const key = k as IndicatorKey;
    if (!seen.has(key)) {
      seen.add(key);
      ordered.push(key);
    }
  }
  return ordered;
}

export interface IndicatorOutputs {
  sma_20?: ScalarSeries;
  sma_50?: ScalarSeries;
  sma_200?: ScalarSeries;
  ema_20?: ScalarSeries;
  ema_50?: ScalarSeries;
  ema_200?: ScalarSeries;
  rsi_14?: ScalarSeries;
  macd_12_26_9?: MacdPoint[];
  bb_20_2?: BollingerPoint[];
  atr_14?: ScalarSeries;
  vwap?: ScalarSeries;
}

/**
 * Compute the requested indicators against a chronologically-ascending
 * bar series. Output keys mirror input `keys`; absent inputs produce
 * absent outputs (no empty arrays).
 *
 * Bars must be in ascending-date order (matches what
 * `GET /v1/prices/{ticker}` returns). Indicators that need OHLC fields
 * (ATR, VWAP) fall back to `close` when `open/high/low` are missing —
 * the prices schema marks them optional via `.passthrough()`.
 *
 * VWAP semantics: the lib's VWAP is cumulative from input[0], so on a
 * 3-month daily window this produces a 3-month-anchored VWAP. That
 * matches what most charting platforms render for "VWAP on daily" —
 * a rolling-window-anchored mean of typical price weighted by volume.
 */
export function computeIndicators(
  keys: IndicatorKey[],
  bars: OhlcvBar[],
): IndicatorOutputs {
  if (keys.length === 0 || bars.length === 0) return {};

  const closes = bars.map((b) => b.close);
  const highs = bars.map((b) => b.high ?? b.close);
  const lows = bars.map((b) => b.low ?? b.close);
  const volumes = bars.map((b) => b.volume ?? 0);
  const n = bars.length;

  const out: IndicatorOutputs = {};

  for (const key of keys) {
    switch (key) {
      case "sma_20":
        out.sma_20 = padScalar(SMA.calculate({ period: 20, values: closes }), n);
        break;
      case "sma_50":
        out.sma_50 = padScalar(SMA.calculate({ period: 50, values: closes }), n);
        break;
      case "sma_200":
        out.sma_200 = padScalar(SMA.calculate({ period: 200, values: closes }), n);
        break;
      case "ema_20":
        out.ema_20 = padScalar(EMA.calculate({ period: 20, values: closes }), n);
        break;
      case "ema_50":
        out.ema_50 = padScalar(EMA.calculate({ period: 50, values: closes }), n);
        break;
      case "ema_200":
        out.ema_200 = padScalar(EMA.calculate({ period: 200, values: closes }), n);
        break;
      case "rsi_14":
        out.rsi_14 = padScalar(RSI.calculate({ period: 14, values: closes }), n);
        break;
      case "macd_12_26_9": {
        const raw = MACD.calculate({
          values: closes,
          fastPeriod: 12,
          slowPeriod: 26,
          signalPeriod: 9,
          // The lib types these as required even though the docs say
          // they default to false. Pass them explicitly to use the
          // standard EMA-based MACD (not the SMA-based variant).
          SimpleMAOscillator: false,
          SimpleMASignal: false,
        });
        out.macd_12_26_9 = padMacd(
          raw.map((p) => ({
            macd: finiteOrNull(p.MACD),
            signal: finiteOrNull(p.signal),
            histogram: finiteOrNull(p.histogram),
          })),
          n,
        );
        break;
      }
      case "bb_20_2": {
        const raw = BollingerBands.calculate({
          period: 20,
          stdDev: 2,
          values: closes,
        });
        out.bb_20_2 = padBollinger(
          raw.map((p) => ({
            upper: finiteOrNull(p.upper),
            middle: finiteOrNull(p.middle),
            lower: finiteOrNull(p.lower),
          })),
          n,
        );
        break;
      }
      case "atr_14":
        out.atr_14 = padScalar(
          ATR.calculate({
            period: 14,
            high: highs,
            low: lows,
            close: closes,
          }),
          n,
        );
        break;
      case "vwap":
        out.vwap = padScalar(
          VWAP.calculate({
            high: highs,
            low: lows,
            close: closes,
            volume: volumes,
          }),
          n,
        );
        break;
    }
  }

  return out;
}

function finiteOrNull(v: number | undefined | null): number | null {
  if (v == null) return null;
  return Number.isFinite(v) ? v : null;
}

function padScalar(values: readonly number[], targetLen: number): ScalarSeries {
  const cleaned: ScalarSeries = values.map((v) => finiteOrNull(v));
  if (cleaned.length >= targetLen) return cleaned.slice(0, targetLen);
  const padCount = targetLen - cleaned.length;
  const padded: ScalarSeries = new Array<number | null>(padCount).fill(null);
  return padded.concat(cleaned);
}

function padMacd(values: MacdPoint[], targetLen: number): MacdPoint[] {
  if (values.length >= targetLen) return values.slice(0, targetLen);
  const padCount = targetLen - values.length;
  const padded: MacdPoint[] = [];
  for (let i = 0; i < padCount; i++) {
    padded.push({ macd: null, signal: null, histogram: null });
  }
  return padded.concat(values);
}

function padBollinger(
  values: BollingerPoint[],
  targetLen: number,
): BollingerPoint[] {
  if (values.length >= targetLen) return values.slice(0, targetLen);
  const padCount = targetLen - values.length;
  const padded: BollingerPoint[] = [];
  for (let i = 0; i < padCount; i++) {
    padded.push({ upper: null, middle: null, lower: null });
  }
  return padded.concat(values);
}
